import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

export const exportBranchWiseLoanExcel = async (loanData, date) => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Loan Audit');

    const ranges = [
        '50K-100K',
        '100k - 200k',
        '200k - 300k',
        '300k - 400k',
        '400k - 500k',
        '500K & Above'
    ];

    // ===== Styles =====
    const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '5B9BD5' } },
        alignment: { vertical: 'middle', horizontal: 'center' },
        border: {
            top: { style: 'thin' }, bottom: { style: 'thin' },
            left: { style: 'thin' }, right: { style: 'thin' }
        }
    };
    const altFill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'D9D9D9' } // light grey
    };

    // ---- First row ----
    const row1 = ['serial no', 'Loan Type', 'Code'];
    loanData.forEach(branch => {
        row1.push(...Array(ranges.length * 2).fill(branch.branchName));
    });
    sheet.addRow(row1);

    // ---- Second row ----
    const row2 = ['', '', ''];
    loanData.forEach(() => {
        const displayDate = `As of ${date.toISOString().split('T')[0]}`;
        const auditDate = `Audit Date ${new Date().toISOString().split('T')[0]}`;

        row2.push(...Array(ranges.length).fill(displayDate)); // As of .../12/31
        row2.push(...Array(ranges.length).fill(auditDate)); // As of audit date
    });
    sheet.addRow(row2);

    // ---- Third row ----
    const row3 = ['', '', ''];
    loanData.forEach(() => {
        row3.push(...ranges); // under As of …/12/31
        row3.push(...ranges); // under As of audit date
    });
    sheet.addRow(row3);

    // Merge "Loan Type" and "Code"
    sheet.mergeCells(1, 1, 3, 1);
    sheet.mergeCells(1, 2, 3, 2);
    sheet.mergeCells(1, 3, 3, 3);

    // Merge branch headers + sub headers
    let colStart = 4;
    loanData.forEach(() => {
        sheet.mergeCells(1, colStart, 1, colStart + ranges.length * 2 - 1);
        sheet.mergeCells(2, colStart, 2, colStart + ranges.length - 1); // As of .../12/31
        sheet.mergeCells(2, colStart + ranges.length, 2, colStart + ranges.length * 2 - 1); // Audit date
        colStart += ranges.length * 2;
    });

    // Add vertical separator line after each branch block
    colStart = 4;
    loanData.forEach(() => {
        const lastCol = colStart + ranges.length * 2 - 1;

        // Apply right border for rows 1–3 (headers)
        for (let r = 1; r <= 3; r++) {
            sheet.getRow(r).getCell(lastCol).border = {
                ...sheet.getRow(r).getCell(lastCol).border,
                right: { style: 'thick', color: { argb: 'FFFF00' } } // yellow line
            };
        }

        colStart += ranges.length * 2;
    });


    // Apply header styles
    [1, 2, 3].forEach(r => {
        sheet.getRow(r).eachCell(cell => {
            cell.style = headerStyle;
        });
    });

    // Freeze top 3 rows + first 2 columns
    sheet.views = [{ state: 'frozen', xSplit: 3, ySplit: 3 }];

    // Collect all loan types
    const loanTypeMap = new Map();
    loanData.forEach(branch => {
        branch.typeWithAmountDtoList.forEach(type => {
            loanTypeMap.set(type.typeCode, type.typeName);
        });
    });

    // Build rows
    let rowIndex = 4;
    let serialNo = 1;

    for (const [typeCode, typeName] of loanTypeMap.entries()) {
        const row = [serialNo++, typeName, typeCode];

        loanData.forEach(branch => {
            const type = branch.typeWithAmountDtoList.find(t => t.typeCode === typeCode);

            const asOfMap = {};
            const auditMap = {};
            ranges.forEach(r => {
                asOfMap[r] = 0;
                auditMap[r] = '';
            });

            if (type) {
                // "As of" counts
                (type.rangeCountDtoList || []).forEach(r => {
                    if (asOfMap[r.range] !== undefined) {
                        asOfMap[r.range] = r.count ?? 0;
                    }
                });

                // "Audit date" counts (may not exist)
                (type.auditDateCountDtoList || []).forEach(r => {
                    if (auditMap[r.range] !== undefined) {
                        auditMap[r.range] = r.count ?? '';
                    }
                });
            }

            row.push(...ranges.map(r => asOfMap[r]));
            row.push(...ranges.map(r => auditMap[r]));
        });

        const addedRow = sheet.addRow(row);

        // Alternate grey shading
        if ((rowIndex - 3) % 2 === 0) {
            addedRow.eachCell(cell => {
                cell.fill = altFill;
            });
        }
        colStart = 4;
        loanData.forEach(() => {
            const lastCol = colStart + ranges.length * 2 - 1;
            addedRow.getCell(lastCol).border = {
                ...addedRow.getCell(lastCol).border,
                right: { style: 'thick', color: { argb: 'FFFF00' } }
            };
            colStart += ranges.length * 2;
        });

        rowIndex++;
    }

    // Auto-fit column width
    sheet.columns.forEach(col => {
        let maxLength = 10;
        col.eachCell({ includeEmpty: true }, cell => {
            const len = cell.value ? cell.value.toString().length : 0;
            if (len > maxLength) maxLength = len;
        });
        col.width = maxLength + 2;
    });

    // Export
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'LoanAudit.xlsx');
};
